import { Component, computed, input } from '@angular/core';
import { NgClass } from '@angular/common';
import { WithdrawStatusLabel } from '../../../_interfaces/fund-account.interface';
import { CardStatus, CardStatusLabel } from '../../../_interfaces/cards.interface';
import {
  TransactionReceiptStatus,
  TransactionReceiptStatusLabel,
  TransactionStatus,
  TransactionStatusLabel,
} from '../../../_interfaces/transactions.interface';
import { CardScheduleStatus, CardScheduleStatusLabel } from '../../../_interfaces/card-topups.interface';
import { EmployeeIdentityDocumentStatus } from '../../../_interfaces/employee.interface';
import { TermsStatus } from '../../../_interfaces/terms.interface';
import { AccountingConnectionStatus } from '../../../_interfaces/accounting.interface';

export enum statusBadgeDepartments {
  WITHDRAWALS,
  CARDS,
  TRANSACTIONS,
  RECEIPTS,
  TOPUPS,
  ID_VERIFICATION,
  DOCUMENTS,
  INTEGRATIONS,
}

// 1. Define the abstract colours available
type BadgeColour = 'green' | 'red' | 'orange' | 'blue' | 'gray';

// 2. The config now uses the abstract colour type instead of a raw string
interface BadgeConfig {
  label: string;
  colour: BadgeColour;
}

@Component({
  selector: 'app-status-badge',
  standalone: true,
  imports: [NgClass],
  templateUrl: './status-badge.component.html',
})
export class StatusBadgeComponent {
  protected readonly statusBadgeDepartments = statusBadgeDepartments;

  value = input.required<
    | string
    | CardStatus
    | TransactionStatus
    | TransactionReceiptStatus
    | CardScheduleStatus
    | EmployeeIdentityDocumentStatus
    | TermsStatus
    | AccountingConnectionStatus
    | null
    | undefined
  >();
  department = input.required<statusBadgeDepartments>();

  /**
   * Computed signal determines the label and abstract colour state.
   */
  protected badge = computed<BadgeConfig>(() => {
    const val = this.value();
    const dept = this.department();

    switch (dept) {
      case statusBadgeDepartments.WITHDRAWALS:
        return this.getWithdrawalConfig(val as string);
      case statusBadgeDepartments.CARDS:
        return this.getCardConfig(val as CardStatus);
      case statusBadgeDepartments.TRANSACTIONS:
        return this.getTransactionConfig(val as TransactionStatusLabel);
      case statusBadgeDepartments.RECEIPTS:
        return this.getTransactionReceiptConfig(val as TransactionReceiptStatus);
      case statusBadgeDepartments.TOPUPS:
        return this.getTopupConfig(val as CardScheduleStatus);
      case statusBadgeDepartments.ID_VERIFICATION:
        return this.getIdVerificationConfig(val as EmployeeIdentityDocumentStatus);
      case statusBadgeDepartments.DOCUMENTS:
        return this.getDocumentsConfig(val as TermsStatus);
      case statusBadgeDepartments.INTEGRATIONS:
        return this.getIntegrationsConfig(val as AccountingConnectionStatus);
      default:
        // Default fallback
        return { label: val?.toString() || '', colour: 'gray' };
    }
  });

  // Helper methods now return just the simple colour name ('green', 'red', etc.)

  private getWithdrawalConfig(v: string): BadgeConfig {
    let colour: BadgeColour = 'gray';
    if (v === WithdrawStatusLabel.APPROVED || v === WithdrawStatusLabel.PROCESSED) {
      colour = 'green';
    } else if (v === WithdrawStatusLabel.PENDING) {
      colour = 'orange';
    } else if (v === WithdrawStatusLabel.EXPIRED) {
      colour = 'red';
    }
    return { label: v, colour };
  }

  private getCardConfig(v: CardStatus): BadgeConfig {
    const mappings: Record<string, BadgeConfig> = {
      [CardStatus.New]: { label: CardStatusLabel.NEW, colour: 'green' },
      [CardStatus.Active]: { label: CardStatusLabel.ACTIVE, colour: 'green' },
      [CardStatus.Blocked]: { label: CardStatusLabel.BLOCKED, colour: 'red' },
      [CardStatus.Removed]: { label: CardStatusLabel.REMOVED, colour: 'gray' },
    };
    return mappings[v] || { label: String(v), colour: 'gray' };
  }

  private getTransactionConfig(v: TransactionStatusLabel): BadgeConfig {
    const mappings: Record<string, BadgeConfig> = {
      [TransactionStatusLabel.NEW]: { label: 'Unsynced', colour: 'gray' },
      [TransactionStatusLabel.READY]: { label: 'Ready to sync', colour: 'blue' },
      [TransactionStatusLabel.ERROR]: { label: 'Sync failed', colour: 'red' },
      [TransactionStatusLabel.SYNCHED]: { label: 'Synced', colour: 'green' },
    };
    return mappings[v] || { label: String(v), colour: 'gray' };
  }

  private getTransactionReceiptConfig(v: TransactionReceiptStatus): BadgeConfig {
    const mappings: Record<string, BadgeConfig> = {
      [TransactionReceiptStatus.PENDING]: { label: TransactionReceiptStatusLabel.PENDING, colour: 'orange' },
      [TransactionReceiptStatus.UPLOADED]: { label: TransactionReceiptStatusLabel.UPLOADED, colour: 'green' },
      [TransactionReceiptStatus.REJECTED]: { label: TransactionReceiptStatusLabel.REJECTED, colour: 'red' },
    };
    return mappings[v] || { label: String(v), colour: 'gray' };
  }

  private getTopupConfig(v: CardScheduleStatus): BadgeConfig {
    const mappings: Record<string, BadgeConfig> = {
      'Stopped': { label: 'Stopped', colour: 'red' },
      [CardScheduleStatus.Pending]: { label: CardScheduleStatusLabel.PENDING, colour: 'orange' },
      [CardScheduleStatus.Active]: { label: CardScheduleStatusLabel.ACTIVE, colour: 'green' },
      [CardScheduleStatus.Completed]: { label: CardScheduleStatusLabel.COMPLETED, colour: 'blue' },
    };
    return mappings[v] || { label: String(v), colour: 'gray' };
  }

  private getIdVerificationConfig(v: EmployeeIdentityDocumentStatus): BadgeConfig {
    const isVerified = v === EmployeeIdentityDocumentStatus.Verified;
    // New, Rejected and Error status gets treated as "Pending"
    return {
      label: isVerified ? 'Done' : 'Pending',
      colour: isVerified ? 'blue' : 'orange',
    };
  }

  private getDocumentsConfig(v: TermsStatus): BadgeConfig {
    const mappings: Record<string, BadgeConfig> = {
      [TermsStatus.Draft]: { label: 'Draft', colour: 'blue' },
      [TermsStatus.Removed]: { label: 'Removed', colour: 'red' },
      [TermsStatus.Published]: { label: 'Published', colour: 'green' },
    };
    return mappings[v] || { label: String(v), colour: 'gray' };
  }

  private getIntegrationsConfig(v: AccountingConnectionStatus): BadgeConfig {
    const mappings: Record<string, BadgeConfig> = {
      [AccountingConnectionStatus.HasTenants]: { label: 'Pending', colour: 'orange' },
      ['noSourceAccount']: { label: 'Pending', colour: 'orange' },
      [AccountingConnectionStatus.Complete]: { label: 'Active', colour: 'green' },
      [AccountingConnectionStatus.AuthError]: { label: 'Authentication Error', colour: 'red' },
      [AccountingConnectionStatus.Error]: { label: 'Connection Error', colour: 'red' },
    };
    return mappings[v] || { label: v, colour: 'gray' };
  }
}
